/**
 * The pack, grouped and measured.
 *
 * **The grouping is the server's, and that is the whole design decision.** Nothing else on
 * the wire can answer *"is this a weapon?"* — `ammo` and `uses_remaining` are capacities, so
 * a taped knife and a brass token are indistinguishable — and the obvious client-side fix, a
 * keyword lexicon over `item` keys, is wrong the first time the DM invents a pneumatic bolt
 * driver and stays wrong silently. So U5 puts `category` on `InventoryItem`, the DM fills it
 * in when it hands the item over, and this module only orders what it is given. Same ban as
 * the character panel's bands: the client does not get to derive a classification the engine owns.
 *
 * `category_label` comes joined and localized (`items.categories.*`), so the headings are not
 * four English strings compiled into the frontend — which they would have been, and would
 * have stayed for every German reader until the i18n pass noticed.
 */
import type { InventoryItem, ItemCategory } from '@/api/generated';
import type { Translate } from '@/i18n/t';

/**
 * Weapons first, then what keeps you alive, then what you carry, then what you are only
 * holding. It is roughly "how fast would you need this", which is the order a player scans in
 * when something has gone wrong.
 */
export const CATEGORY_ORDER = [
  'weapon',
  'consumable',
  'gear',
  'misc',
] as const satisfies readonly ItemCategory[];

export interface ItemGroup {
  category: ItemCategory;
  /** The API's own heading for it, localized. Never spelled here. */
  label: string;
  items: InventoryItem[];
}

/**
 * Non-empty groups, in `CATEGORY_ORDER`, each keeping the order the API sent within it.
 *
 * An empty group is not rendered rather than rendered empty: four headings over one knife is
 * a form again, and the character panel spends the panel's whole budget on not being one.
 */
export function groupInventory(items: readonly InventoryItem[]): ItemGroup[] {
  const groups = new Map<ItemCategory, ItemGroup>();

  for (const entry of items) {
    const group = groups.get(entry.category);
    if (group) group.items.push(entry);
    else
      groups.set(entry.category, {
        category: entry.category,
        label: entry.category_label,
        items: [entry],
      });
  }

  return CATEGORY_ORDER.map((category) => groups.get(category)).filter(
    (group): group is ItemGroup => group !== undefined,
  );
}

export interface Meter {
  /** `ammo` and `uses` are different facts about the same row, so both can be present. */
  kind: 'ammo' | 'uses';
  count: number;
  /** What a screen reader hears — the pips are `aria-hidden` decoration over this. */
  label: string;
}

/**
 * How many pips to draw before the count stops being countable at a glance.
 *
 * Past this the meter has failed at its one job and the number is better. Nothing on the wire
 * carries a *capacity*, so these are counts rather than fractions: eleven rounds is eleven
 * dots, not eleven out of a magazine size the engine has never stored.
 */
export const MAX_PIPS = 10;

export function metersFor(t: Translate, entry: InventoryItem): Meter[] {
  const meters: Meter[] = [];
  if (entry.ammo !== null) {
    meters.push({ kind: 'ammo', count: entry.ammo, label: t.plural('ui.item.rounds', entry.ammo) });
  }
  if (entry.uses_remaining !== null) {
    meters.push({
      kind: 'uses',
      count: entry.uses_remaining,
      label: t.plural('ui.item.uses', entry.uses_remaining),
    });
  }
  return meters;
}
